import type { ReactNode } from 'react';
import { usePageTitlesVisible } from '@hooks/usePageTitlesVisible';
import styles from './PageHeader.module.css';

interface IPageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Beside the title, e.g. a LIVE tag or a MOCK pill. */
  badge?: ReactNode;
  /** The page's own controls, on the right. */
  actions?: ReactNode;
  className?: string;
}

/**
 * Every page's header: one title and subtitle style, the page's controls on
 * the right. With page titles turned off (the user menu), the title and
 * subtitle stay for screen readers but take no space; the controls stay on
 * the right, with the badge beside them, and a header with no controls takes
 * no space at all, so the page starts at the top. A badge alone is part of the
 * title — a LIVE or MOCK tag on a row of its own would be a strip of nothing.
 */
export function PageHeader({ title, subtitle, badge, actions, className }: IPageHeaderProps) {
  const visible = usePageTitlesVisible();
  // With the titles off, the badge moves to the right with the controls (and only with them).
  const bar = actions !== undefined;
  const barBadge = visible ? undefined : badge;
  const classes = [styles.header, !visible && (bar ? styles.titlesHidden : 'visually-hidden'), className]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={classes}>
      <div className={visible ? styles.text : 'visually-hidden'}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>{title}</h1>
          {visible && badge}
        </div>
        {subtitle !== undefined && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {bar && (
        <div className={styles.bar}>
          {barBadge}
          {actions}
        </div>
      )}
    </header>
  );
}
